/**
 * DatastoreBrowser - Record browser for individual datastores
 *
 * Displays records from a datastore with:
 * - Dynamic field discovery from OpenSearch mappings
 * - Column picker to choose visible fields
 * - Faceted filtering with checkboxes for keyword fields
 * - Sort by any field
 * - Search and cursor-based pagination
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Search,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertTriangle,
  Database,
  Filter,
  X,
  Settings2,
  ChevronsUpDown,
  Info
} from 'lucide-react';
import { browseApi, BrowseField, FacetBucket } from '../services/api';
import { DATASOURCE_COLORS } from '../constants/colors';
import RecordDetailPanel from '../components/RecordDetailPanel';

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 400;

/** Datasource card definitions (energy portal) */
const DATASTORE_CARDS: { key: string; label: string; description: string; source: string }[] = [
  { key: 'aemo', label: 'AEMO Energy', description: 'NEM settlement and dispatch pricing across 5 regions including spot prices, demand, and generation data.', source: 'Australian Energy Market Operator (AEMO) NEMweb' },
  { key: 'aemo-duid', label: 'AEMO DUID', description: 'Generator unit (DUID) output, capacity, bid stacks, rebid reasons, and dispatch interval data.', source: 'AEMO NEMweb DUID data feeds' },
  { key: 'aemo-fcas', label: 'AEMO FCAS', description: 'Frequency control ancillary services across all 8 FCAS markets: raise/lower for 6s, 60s, 5min, and regulation.', source: 'AEMO FCAS market data' },
  { key: 'bom', label: 'BOM Weather', description: 'Weather observations from stations near NEM generators including temperature, rainfall, wind, and solar irradiance.', source: 'Bureau of Meteorology (BOM) observation network' },
  { key: 'aer', label: 'AER Regulatory', description: 'Regulatory determinations, pricing guidelines, compliance documents, and rule changes.', source: 'Australian Energy Regulator (AER)' },
  { key: 'regulatory', label: 'Regulatory Docs', description: 'Full regulatory submission documents covering Capex, Opex, RAB, and service classification.', source: 'AER, AEMC, AEMO regulatory frameworks' },
  { key: 'scholarly', label: 'Scholarly Research', description: 'Academic papers on energy systems, grid modernisation, renewables integration, and market design.', source: 'OpenAlex, Semantic Scholar, arXiv' },
  { key: 'energy-organisations', label: 'Energy Organisations', description: 'Global energy sector organisations including generators, retailers, TNSPs, DNSPs, and market bodies.', source: 'AEMO, CER, EIA, GEM, Wikidata' },
  { key: 'grid-connection-queue', label: 'Grid Connection Queue', description: 'Connection enquiries, applications, and committed generation/storage projects awaiting grid access.', source: 'AEMO generation connection queue' },
  { key: 'rez-capacity', label: 'REZ Capacity', description: 'Renewable Energy Zone hosting capacity, declared REZ areas, and allocated MW by region.', source: 'AEMO ISP and state REZ roadmaps' },
  { key: 'ppa-intelligence', label: 'PPA Intelligence', description: 'Power Purchase Agreement pricing benchmarks, LGC spot prices, contract data, and public indices.', source: 'Public PPA disclosures and LGC market data' },
  { key: 'network-constraints', label: 'Network Constraints', description: 'Binding and violating constraint equations, thermal limits, and TNSP capability across the NEM.', source: 'AEMO NEMweb constraint data and TNSP reports' },
  { key: 'planning-approvals', label: 'Planning Approvals', description: 'State and federal planning approvals for energy infrastructure including conditions and timelines.', source: 'NSW Major Projects, VIC DELWP, QLD DAF, SA planning portals' },
  { key: 'construction-milestones', label: 'Construction Milestones', description: 'Construction status from financial close through commissioning to commercial operation.', source: 'AEMO, CEC project tracker, ASX announcements' },
  { key: 'community-sentiment', label: 'Community Sentiment', description: 'Community feedback, social licence indicators, and stakeholder engagement outcomes for energy projects.', source: 'Council submissions, news media, social media analysis' },
  { key: 'partner', label: 'Partner & Quantum Discovery', description: 'Energy sector partner organisations, facilities, capabilities, and quantum ecosystem entities.', source: 'Quantum Navigator, Nature Index, university databases' },
];

/** Column hints per datastore — dynamic field discovery supplements these */
const DATASTORE_COLUMNS: Record<string, { key: string; label: string }[]> = {
  aemo: [
    { key: 'region', label: 'Region' },
    { key: 'price', label: 'Price' },
    { key: 'date', label: 'Date' },
    { key: 'type', label: 'Type' },
    { key: 'source', label: 'Source' },
  ],
  'aemo-duid': [
    { key: 'region', label: 'Region' },
    { key: 'price', label: 'Price' },
    { key: 'date', label: 'Date' },
    { key: 'type', label: 'Type' },
    { key: 'source', label: 'Source' },
  ],
  'aemo-fcas': [
    { key: 'region', label: 'Region' },
    { key: 'price', label: 'Price' },
    { key: 'date', label: 'Date' },
    { key: 'type', label: 'Type' },
    { key: 'source', label: 'Source' },
  ],
  bom: [
    { key: 'station', label: 'Station' },
    { key: 'observation', label: 'Observation' },
    { key: 'date', label: 'Date' },
    { key: 'region', label: 'Region' },
  ],
  aer: [
    { key: 'title', label: 'Title' },
    { key: 'category', label: 'Category' },
    { key: 'date', label: 'Date' },
    { key: 'source', label: 'Source' },
  ],
  regulatory: [
    { key: 'title', label: 'Title' },
    { key: 'type', label: 'Type' },
    { key: 'date', label: 'Date' },
    { key: 'jurisdiction', label: 'Jurisdiction' },
  ],
  scholarly: [
    { key: 'title', label: 'Title' },
    { key: 'authors', label: 'Authors' },
    { key: 'journal', label: 'Journal' },
    { key: 'year', label: 'Year' },
    { key: 'citations', label: 'Citations' },
  ],
  'energy-organisations': [
    { key: 'name', label: 'Name' },
    { key: 'country', label: 'Country' },
    { key: 'category', label: 'Category' },
    { key: 'facility_type', label: 'Facility Type' },
  ],
  'grid-connection-queue': [
    { key: 'project_name', label: 'Project' },
    { key: 'developer', label: 'Developer' },
    { key: 'capacity_mw', label: 'Capacity (MW)' },
    { key: 'fuel_type', label: 'Fuel Type' },
    { key: 'status', label: 'Status' },
  ],
  'rez-capacity': [
    { key: 'rez_name', label: 'REZ Name' },
    { key: 'state', label: 'State' },
    { key: 'hosting_capacity_mw', label: 'Hosting (MW)' },
    { key: 'available_capacity_mw', label: 'Available (MW)' },
  ],
  'ppa-intelligence': [
    { key: 'contract_type', label: 'Contract Type' },
    { key: 'technology', label: 'Technology' },
    { key: 'region', label: 'Region' },
    { key: 'price_aud_mwh', label: 'Price (AUD/MWh)' },
  ],
  'network-constraints': [
    { key: 'constraint_id', label: 'Constraint ID' },
    { key: 'region', label: 'Region' },
    { key: 'limit_mw', label: 'Limit (MW)' },
    { key: 'binding_hours', label: 'Binding Hours' },
  ],
  'planning-approvals': [
    { key: 'project_name', label: 'Project' },
    { key: 'state', label: 'State' },
    { key: 'status', label: 'Status' },
    { key: 'type', label: 'Type' },
  ],
  'construction-milestones': [
    { key: 'project_name', label: 'Project' },
    { key: 'milestone', label: 'Milestone' },
    { key: 'status', label: 'Status' },
    { key: 'date', label: 'Date' },
  ],
  'community-sentiment': [
    { key: 'project_name', label: 'Project' },
    { key: 'sentiment', label: 'Sentiment' },
    { key: 'source', label: 'Source' },
    { key: 'date', label: 'Date' },
  ],
  partner: [
    { key: 'name', label: 'Name' },
    { key: 'country', label: 'Country' },
    { key: 'category', label: 'Category' },
    { key: 'facility_type', label: 'Facility Type' },
  ],
};

// Fields to hide by default (large text / internal)
const DEFAULT_HIDDEN = new Set([
  '_id', 'full_text', 'abstract', 'description', 'content', 'text', 'body',
  'summary', 'prescribing_text', 'li_html_text', 'schedule_html_text',
  'chunk_id', 'chunk_index', 'metadata', 'timestamp',
]);

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const DatastoreBrowser: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedRecord, setSelectedRecord] = useState<Record<string, any> | null>(null);
  const [searchAfterCursor, setSearchAfterCursor] = useState<any[] | undefined>();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Dynamic field state
  const [availableFields, setAvailableFields] = useState<BrowseField[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [fieldsLoaded, setFieldsLoaded] = useState(false);
  const columnPickerRef = useRef<HTMLDivElement>(null);

  // Faceted filter state
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});
  const [facetData, setFacetData] = useState<Record<string, FacetBucket[]>>({});
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [expandedFacets, setExpandedFacets] = useState<Set<string>>(new Set());

  // Debounce search input
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(1);
      setSearchAfterCursor(undefined);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  // Close column picker on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (columnPickerRef.current && !columnPickerRef.current.contains(e.target as Node)) {
        setShowColumnPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const dsCard = DATASTORE_CARDS.find(c => c.key === name);
  const dsColor = DATASOURCE_COLORS[name || ''] || DATASOURCE_COLORS.custom || { hex: '#6B7280' };
  const hardcodedColumns = DATASTORE_COLUMNS[name || ''] || [];

  // Fetch available fields on mount
  useEffect(() => {
    if (!name) return;
    let cancelled = false;
    (async () => {
      try {
        const fields = await browseApi.getFields(name);
        if (cancelled) return;
        setAvailableFields(fields);

        // Default visible columns: use hardcoded if they match real fields, else use non-large, non-hidden fields
        const fieldNames = new Set(fields.map(f => f.name));
        const hardcodedMatch = hardcodedColumns.filter(c => fieldNames.has(c.key));

        if (hardcodedMatch.length >= 2) {
          setVisibleColumns(hardcodedMatch.map(c => c.key));
        } else {
          const auto = fields
            .filter(f => !f.large && !DEFAULT_HIDDEN.has(f.name))
            .slice(0, 6)
            .map(f => f.name);
          setVisibleColumns(auto);
        }
        setFieldsLoaded(true);
      } catch (err) {
        console.warn('Failed to fetch fields, using hardcoded:', err);
        setVisibleColumns(hardcodedColumns.map(c => c.key));
        setFieldsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [name]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyword fields for faceted filtering
  const keywordFields = useMemo(() =>
    availableFields.filter(f => f.keyword && !f.large && !DEFAULT_HIDDEN.has(f.name)),
    [availableFields]
  );

  // Build facetFields for the query — request facets for all keyword fields
  const facetFieldNames = useMemo(() => keywordFields.map(f => f.name), [keywordFields]);

  // Fetch records
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['browse', name, searchQuery, page, sortField, sortOrder, searchAfterCursor, activeFilters, facetFieldNames],
    queryFn: async () => {
      const result = await browseApi.browse(name || '', {
        query: searchQuery || undefined,
        page,
        pageSize: PAGE_SIZE,
        sortField,
        sortOrder,
        searchAfter: searchAfterCursor,
        filters: Object.keys(activeFilters).length > 0 ? activeFilters : undefined,
        facetFields: facetFieldNames.length > 0 ? facetFieldNames : undefined,
      });
      // Update facet data from response
      if (result.facets) {
        setFacetData(result.facets);
      }
      return result;
    },
    staleTime: 30000,
    enabled: !!name && fieldsLoaded,
  });

  const results = data?.results || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Column labels — prefer hardcoded label, else title-case the field name
  const getColumnLabel = useCallback((fieldName: string): string => {
    const hardcoded = hardcodedColumns.find(c => c.key === fieldName);
    if (hardcoded) return hardcoded.label;
    return fieldName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }, [hardcodedColumns]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setPage(1);
    setSearchAfterCursor(undefined);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    clearTimeout(debounceRef.current);
    setSearchQuery(searchInput);
    setPage(1);
    setSearchAfterCursor(undefined);
  };

  const toggleColumn = (fieldName: string) => {
    setVisibleColumns(prev =>
      prev.includes(fieldName)
        ? prev.filter(c => c !== fieldName)
        : [...prev, fieldName]
    );
  };

  const toggleFilter = (field: string, value: string) => {
    setActiveFilters(prev => {
      const current = prev[field] || [];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      if (updated.length === 0) {
        const copy = { ...prev };
        delete copy[field];
        return copy;
      }
      return { ...prev, [field]: updated };
    });
    setPage(1);
    setSearchAfterCursor(undefined);
  };

  const clearFilters = () => {
    setActiveFilters({});
    setPage(1);
    setSearchAfterCursor(undefined);
  };

  const toggleFacetExpand = (field: string) => {
    setExpandedFacets(prev => {
      const copy = new Set(prev);
      if (copy.has(field)) copy.delete(field);
      else copy.add(field);
      return copy;
    });
  };

  const activeFilterCount = Object.values(activeFilters).reduce((sum, vals) => sum + vals.length, 0);

  if (!name) {
    return (
      <div className="text-center py-12 text-gray-500">
        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
        <p>No datastore specified</p>
        <Link to="/" className="text-sm text-blue-600 hover:underline mt-2 inline-block">Back to DataStores</Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/" className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </Link>
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: dsColor.hex + '20' }}
          >
            <Database className="w-6 h-6" style={{ color: dsColor.hex }} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{dsCard?.label || name}</h2>
            <p className="text-sm text-gray-500">
              {total > 0 ? `${formatNumber(total)} records` : 'Browse records'}
            </p>
          </div>
        </div>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-gray-400 ml-auto" />}
      </div>

      {/* Datastore description */}
      {dsCard && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-gray-700 leading-relaxed">{dsCard.description}</p>
            <p className="text-xs text-gray-400 mt-1">Source: {dsCard.source}</p>
          </div>
        </div>
      )}

      {/* Search & Controls bar */}
      <div className="bg-gray-100 rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search form */}
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Search records..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': dsColor.hex } as any}
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: dsColor.hex }}
            >
              Search
            </button>
          </form>

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilterPanel(prev => !prev)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
              showFilterPanel || activeFilterCount > 0
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-white text-gray-900 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Column picker */}
          <div className="relative" ref={columnPickerRef}>
            <button
              onClick={() => setShowColumnPicker(prev => !prev)}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                showColumnPicker
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Settings2 className="w-4 h-4" />
              Columns
            </button>

            {showColumnPicker && (
              <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-xl border border-gray-200 shadow-xl z-50 max-h-80 overflow-y-auto">
                <div className="p-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Visible Columns</p>
                </div>
                <div className="p-2">
                  {availableFields
                    .filter(f => !DEFAULT_HIDDEN.has(f.name) && !f.large)
                    .map(field => (
                      <label
                        key={field.name}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns.includes(field.name)}
                          onChange={() => toggleColumn(field.name)}
                          className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                        />
                        <span className="text-sm text-gray-700 truncate">{getColumnLabel(field.name)}</span>
                        {field.keyword && (
                          <span className="text-[10px] text-gray-400 ml-auto">filterable</span>
                        )}
                      </label>
                    ))}
                  {/* Large/hidden fields in a separate section */}
                  {availableFields.filter(f => f.large || DEFAULT_HIDDEN.has(f.name)).length > 0 && (
                    <>
                      <div className="border-t border-gray-100 my-1" />
                      <p className="text-[10px] text-gray-400 px-2 py-1 uppercase">Large / Internal</p>
                      {availableFields
                        .filter(f => (f.large || DEFAULT_HIDDEN.has(f.name)) && f.name !== '_id')
                        .map(field => (
                          <label
                            key={field.name}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={visibleColumns.includes(field.name)}
                              onChange={() => toggleColumn(field.name)}
                              className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                            />
                            <span className="text-sm text-gray-400 truncate">{getColumnLabel(field.name)}</span>
                          </label>
                        ))}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Active filters summary */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className="text-xs text-gray-500">Active filters:</span>
            {Object.entries(activeFilters).map(([field, values]) =>
              values.map(val => (
                <button
                  key={`${field}-${val}`}
                  onClick={() => toggleFilter(field, val)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-800 text-white hover:bg-gray-700"
                >
                  {getColumnLabel(field)}: {val}
                  <X className="w-3 h-3" />
                </button>
              ))
            )}
            <button
              onClick={clearFilters}
              className="text-xs text-red-600 hover:underline ml-1"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Main content area */}
      <div className="flex gap-4">
        {/* Faceted filter sidebar */}
        {showFilterPanel && keywordFields.length > 0 && (
          <div className="w-64 flex-shrink-0 bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Filter By</h3>
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} className="text-xs text-red-600 hover:underline">
                  Clear
                </button>
              )}
            </div>
            {keywordFields.map(field => {
              const buckets = facetData[field.name] || [];
              const isExpanded = expandedFacets.has(field.name);
              const selectedValues = activeFilters[field.name] || [];
              const displayBuckets = isExpanded ? buckets : buckets.slice(0, 8);

              if (buckets.length === 0) return null;

              return (
                <div key={field.name} className="border-b border-gray-200 pb-3 last:border-0">
                  <button
                    onClick={() => toggleFacetExpand(field.name)}
                    className="flex items-center justify-between w-full text-left mb-1.5"
                  >
                    <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      {getColumnLabel(field.name)}
                    </span>
                    <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                  <div className="space-y-0.5">
                    {displayBuckets.map(bucket => {
                      const isChecked = selectedValues.includes(bucket.value);
                      return (
                        <label
                          key={bucket.value}
                          className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-gray-100 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleFilter(field.name, bucket.value)}
                            className="rounded border-gray-300 text-gray-900 focus:ring-gray-900 w-3.5 h-3.5"
                          />
                          <span className="text-xs text-gray-700 truncate flex-1" title={bucket.value}>
                            {bucket.value || '(empty)'}
                          </span>
                          <span className="text-[10px] text-gray-400">{formatNumber(bucket.count)}</span>
                        </label>
                      );
                    })}
                    {buckets.length > 8 && (
                      <button
                        onClick={() => toggleFacetExpand(field.name)}
                        className="text-[10px] text-blue-600 hover:underline px-1 mt-0.5"
                      >
                        {isExpanded ? 'Show less' : `+${buckets.length - 8} more`}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Results table */}
        <div className="flex-1 bg-gray-100 rounded-xl border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: dsColor.hex }} />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 text-red-500">
              <AlertTriangle className="w-10 h-10 mb-2" />
              <p className="text-sm">Failed to load records</p>
              <p className="text-xs text-gray-500 mt-1">{error instanceof Error ? error.message : 'Unknown error'}</p>
            </div>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <Database className="w-10 h-10 mb-2 text-gray-300" />
              <p className="text-sm">{searchQuery || activeFilterCount > 0 ? 'No matching records found' : 'No records available'}</p>
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} className="text-sm text-blue-600 hover:underline mt-2">
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200" style={{ backgroundColor: dsColor.hex + '08' }}>
                      {visibleColumns.map(col => (
                        <th
                          key={col}
                          onClick={() => handleSort(col)}
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none whitespace-nowrap"
                        >
                          <div className="flex items-center gap-1">
                            {getColumnLabel(col)}
                            {sortField === col && (
                              sortOrder === 'asc'
                                ? <ChevronUp className="w-3.5 h-3.5" />
                                : <ChevronDown className="w-3.5 h-3.5" />
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {results.map((record, idx) => (
                      <tr
                        key={record._id || idx}
                        onClick={() => setSelectedRecord(record)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        {visibleColumns.map(col => {
                          const val = record[col];
                          let display: string;
                          if (val == null || val === '') {
                            display = '—';
                          } else if (Array.isArray(val)) {
                            display = val.join(', ');
                          } else if (typeof val === 'object') {
                            display = JSON.stringify(val);
                          } else {
                            display = String(val);
                          }
                          return (
                            <td key={col} className="px-4 py-3 text-gray-800 max-w-xs truncate" title={display !== '—' ? display : undefined}>
                              {display}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div className="text-xs text-gray-500">
                  Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {formatNumber(total)}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setPage(1); setSearchAfterCursor(undefined); }}
                    disabled={page <= 1}
                    className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-gray-600 font-medium">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => {
                      if (data?.searchAfter) setSearchAfterCursor(data.searchAfter);
                      setPage(p => Math.min(totalPages, p + 1));
                    }}
                    disabled={page >= totalPages}
                    className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Record detail panel */}
      {selectedRecord && (
        <RecordDetailPanel
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
          accentColor={dsColor.hex}
        />
      )}
    </div>
  );
};

export default DatastoreBrowser;
